import { types, Instance } from 'mobx-state-tree';
import { isEmpty } from 'lodash';
import { JobBoardModel } from '@src/features/job/models/JobsStore/JobBoardModel';
import { JOB_STATES_MAP } from 'src/constants';
import { formatOffice } from 'src/helpers';
import { getJobId } from '../../helpers';
import { ApplicationFormSettingsModel } from './ApplicationFormSettingsModel';
import { CompanyMemberModel, JobMemberModel } from './JobMemberModel';
import { SuggestionSettingsModel } from './SuggestionSettingsModel';

export const JobModel = types
  .model('Job', {
    id: types.maybe(types.number),
    ability_token: types.maybeNull(types.string),
    is_applied: types.maybe(types.boolean),
    state: types.maybeNull(types.enumeration(Object.keys(JOB_STATES_MAP))),
    position_title: types.maybeNull(types.string),
    visible_on_board: types.maybeNull(types.boolean),
    is_anonymous: types.maybeNull(types.boolean),
    external_url: types.maybeNull(types.string),
    allow_external_url: types.maybeNull(types.boolean),
    employment_type: types.maybeNull(types.string),
    brief: types.maybeNull(types.string),
    deadline: types.maybeNull(types.string),
    start_date: types.maybeNull(types.string),
    candidate_is_trusted: types.optional(types.boolean, false),
    length: types.maybeNull(types.number),
    currency: types.maybeNull(types.string),
    experiences: types.maybeNull(types.frozen(types.array(types.union(types.string, types.null)))),
    job_functions: types.maybeNull(types.frozen(types.array(types.string))),
    salary: types.maybeNull(types.string),
    link: types.maybeNull(types.string),
    bonus_commission: types.maybeNull(types.boolean),
    equity_compensation: types.maybeNull(types.boolean),
    external_recruiter: types.maybeNull(types.boolean),
    is_external: types.optional(types.union(types.boolean, types.null), false),
    external_link: types.optional(types.union(types.string, types.null), ''),
    external_contact_info: types.optional(types.union(types.string, types.null), ''),
    team: types.maybeNull(types.frozen()),
    office: types.maybeNull(types.frozen()),
    company: types.maybeNull(types.frozen()),
    company_size: types.optional(types.maybeNull(types.string), ''),
    main_theme: types.maybeNull(types.frozen()),
    sub_themes: types.maybeNull(types.array(types.frozen())),
    focuses: types.maybeNull(types.array(types.frozen())),
    preferences_url: types.maybeNull(types.string),
    application_form_settings: types.maybeNull(ApplicationFormSettingsModel),
    suggestionSettings: types.maybeNull(SuggestionSettingsModel),
    advertising: types.maybeNull(types.array(JobBoardModel)),
    feeds: types.maybeNull(types.frozen()),
    company_users: types.maybeNull(types.array(CompanyMemberModel)),
    job_users: types.maybeNull(types.array(JobMemberModel)),
    similar_jobs: types.maybeNull(types.frozen([])),
    ideal_profile_sub_themes: types.maybeNull(types.frozen([])),
    ideal_profile_focuses: types.maybeNull(types.frozen([])),
    accessPolicies: types.frozen<{
      jobs?: boolean;
      application_forms?: boolean;
      suggestions_settings?: boolean;
      advertising?: boolean;
      feeds?: boolean;
      members?: boolean;
    }>({})
  })
  .views(self => ({
    get cid() {
      return getJobId(self);
    },
    get officeLocation() {
      return formatOffice(self.office);
    },
    get skipExternalUrl() {
      return skipExternalUrl(self);
    }
  }));

// detect when job doesn't use external url
export const skipExternalUrl = (job): boolean => !(job.allow_external_url && !isEmpty(job.external_url));

export type IJob = Instance<typeof JobModel>;
